import React, { createContext, useContext, useEffect, useState } from 'react'
import { useSession } from './betterAuthClient'
import { getGuestAuthToken } from './guestAuth'
import { setToken } from '@/api/http-service/PrepareAPIService'

interface AuthContextType {
  isAuthenticated: boolean
  isGuest: boolean
  user: any | null
  token: string | null
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: session, isPending: sessionLoading } = useSession()
  const [guestToken, setGuestToken] = useState<string | null>(null)
  const [prepareToken, setPrepareToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [tokenLoading, setTokenLoading] = useState(false)

  useEffect(() => {
    const initAuth = async () => {
      try {
        // TEMPORARILY DISABLED: Guest auth for testing better-auth
        // If no authenticated session, try to get guest token
        // if (!session) {
        //   const token = await getGuestAuthToken()
        //   setGuestToken(token)
        // }

        // For now, just check if we have an existing guest token in localStorage
        if (!session && typeof window !== 'undefined') {
          const existingToken = localStorage.getItem('prepareGuestAuthToken')
          if (existingToken) {
            setGuestToken(existingToken)
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (!sessionLoading) {
      initAuth()
    }
  }, [session, sessionLoading])

  // Set the token for API calls whenever auth state changes
  useEffect(() => {
    const fetchPrepareToken = async () => {
      if (session?.user) {
        setTokenLoading(true)
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4001'
          const baseApiUrl = apiUrl.replace(/\/prepare\/?$/, '')
          const tokenUrl = `${baseApiUrl}/auth/prepare-token`

          const response = await fetch(tokenUrl, {
            credentials: 'include', // Include Better Auth cookies
          })

          if (response.ok) {
            const data = await response.json()
            setPrepareToken(data.token)
            setToken(data.token)
          } else {
            const errorText = await response.text()
            console.error(
              'Failed to get prepare-api token:',
              response.status,
              errorText
            )
          }
        } catch (error) {
          console.error('Error fetching prepare-api token:', error)
        } finally {
          setTokenLoading(false)
        }
      } else if (guestToken) {
        setToken(guestToken)
      } else {
      }
    }

    fetchPrepareToken()
  }, [session?.user, guestToken])

  const value: AuthContextType = {
    isAuthenticated: !!session || !!guestToken,
    isGuest: !session && !!guestToken,
    user: session?.user || null,
    token: prepareToken || guestToken,
    isLoading: sessionLoading || isLoading || tokenLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
